import React from 'react';
import { Disclosure } from '@headlessui/react';
import { ChevronUpIcon } from '@heroicons/react/solid';
import {
  ClockIcon,
  ShieldCheckIcon,
  TagIcon,
  MailIcon,
} from '@heroicons/react/outline';
import { toMoney } from '@utils/helpers';
import Link from 'next/link';
import { MicroTrustBox } from '@components/TrustPilot';
import { MarkdownContent } from '@components/MarkDownContent';
import { StateBondProductJsonLd, SEO } from '../../common/components/Seo';
import licenses from '../../../data/static-data/state-licenses.json';
import { getSiteData } from '../../../data/bootstrapData';
import { getPreviewProductFromDato } from '../../../data/datocms/getAllProducts';
import { formatConnectUrl } from '@utils/helpers';
import { StateOptions } from 'data/static-data/StateOptions';

export default function ProductPage({ product, license, stateName }) {
  const [selectedAmountId, setSelectedAmountId] = React.useState(
    product?.amounts?.length === 1 ? product?.amounts[0]._id : ''
  );
  const selectedAmount = product?.amounts?.find(
    (a) => a._id === selectedAmountId
  );
  return (
    <>
      <SEO
        title={product.metaFields.title}
        description={product.metaFields.description}
      />
      <StateBondProductJsonLd
        product={product}
        url={`/${stateName}/${product.slug}`}
      />
      <div className="w-full max-w-screen-lg mx-auto px-4 font-ui">
        <nav className="flex py-3" aria-label="Breadcrumb">
          <ol className="flex items-center text-sm">
            <li>
              <div>
                <Link href="/">Home</Link>
              </div>
            </li>
            <li>
              <div className="flex items-center">
                <span className="mx-2">/</span>
                <Link href={`/${stateName}`} legacyBehavior>
                  {product.state}
                </Link>
              </div>
            </li>
            <li>
              <div className="flex items-center text-gray-500">
                <span className="mx-2">/</span>
                <Link href={'#'} legacyBehavior>
                  {product.name}
                </Link>
              </div>
            </li>
          </ol>
        </nav>
        <div className="flex space-x-0 sm:mt-10 mt-5 sm:flex-row flex-col w-full divide-none sm:divide-x">
          <div className="sm:w-7/12 sm:pr-5 pr-0 w-full flex flex-col">
            <div className="w-full max-w-full sm:max-w-screen-xs space-y-5">
              <h1 className="text-4xl tracking-tight font-semibold leading-tight font-brand">
                <small className="block font-medium tracking-normal">
                  {product.state}
                </small>
                {product.name}
              </h1>
              <MicroTrustBox></MicroTrustBox>

              <PriceLabel selectedAmount={selectedAmount}></PriceLabel>
              <div className="text-gray-800 text-base sm:text-lg">
                {product.description && (
                  <MarkdownContent markdown={product.description} />
                )}
              </div>
              <div className="space-y-7">
                <BondAmount
                  product={product}
                  selectedAmountId={selectedAmountId}
                  setSelectedAmountId={setSelectedAmountId}
                ></BondAmount>
                <PurchaseButton
                  selectedAmount={selectedAmount}
                  product={product}
                ></PurchaseButton>
              </div>
            </div>
          </div>
          <ProductPerks product={product}></ProductPerks>
        </div>
      </div>
      <div className="w-full bg-gray-100 font-brand mt-16">
        <div className="w-full max-w-screen-lg mx-auto py-16">
          <AdditionalInfo license={license} product={product}></AdditionalInfo>
        </div>
      </div>

      {product.additionalResources && (
        <div className="max-w-screen-lg mx-auto py-16 bg-white font-brand">
          <h3 className="text-2xl font-semibold">Additional Resources</h3>
          <div className="prose prose-blue">
            <MarkdownContent
              markdown={product.additionalResources}
            ></MarkdownContent>
          </div>
        </div>
      )}
    </>
  );
}

function PurchaseButton({ selectedAmount, product }) {
  let connectUrl = formatConnectUrl({
    bondId: product.sbId,
    amount: selectedAmount?.low || null,
    state: StateOptions.find(
      (state) => state.display.toLowerCase() === product.state.toLowerCase()
    ).value,
  });
  let buttonText = 'Select an Amount';
  if (selectedAmount) {
    buttonText = selectedAmount.instantPurchase ? 'Buy Now' : 'Get Started';
  }

  // TODO: Send some non-PPK instant purchases straight to instant purchase
  // if it's instant purchase and not ppk. Try making the button hit a post route to /application/bondId
  // and send {state:state.abbr, amount:""}

  // if (selectedAmount.instantPurchase && !selectedAmount.isPPK) {

  //   return (
  //     <form
  //       action={`${
  //         process.env.NEXT_PUBLIC_CONNECT_URL
  //       }/application/${product.sbId.toString()}`}
  //       method="post"
  //     >
  //       <input type="hidden" name="state" value={product.state}></input>
  //       <input type="hidden" name="amount" value={selectedAmount.low}></input>
  //       <button
  //         type="submit"
  //         disabled={!selectedAmount}
  //         className="text-white !mt-10 w-full justify-center flex flex-row items-center hover:bg-blue-surety-highlight shadow-lg text-xl  transition-all bg-blue-surety px-4 py-2 rounded-lg"
  //       >
  //         <span>{buttonText}</span>
  //         {selectedAmount?.instantPurchase && (
  //           <svg
  //             className="text-white w-5 ml-2"
  //             xmlns="http://www.w3.org/2000/svg"
  //             fill="none"
  //             viewBox="0 0 24 24"
  //             stroke="currentColor"
  //           >
  //             <path
  //               strokeLinecap="round"
  //               strokeLinejoin="round"
  //               strokeWidth={2}
  //               d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
  //             />
  //           </svg>
  //         )}
  //       </button>
  //     </form>
  //   );
  // }

  return (
    <a href={connectUrl}>
      <button
        disabled={!selectedAmount}
        className="text-white !mt-10 w-full justify-center flex flex-row items-center hover:bg-blue-surety-highlight shadow-lg text-xl  transition-all bg-blue-surety px-4 py-2 rounded-lg"
      >
        <span>{buttonText}</span>
        {selectedAmount?.instantPurchase && (
          <svg
            className="text-white w-5 ml-2"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
            />
          </svg>
        )}
      </button>
    </a>
  );
}

function PriceLabel({ selectedAmount }) {
  let label = '';
  if (selectedAmount) {
    label = 'Pricing Varies';
    if (
      selectedAmount.premium.toUpperCase() !== 'VARIES' &&
      !selectedAmount.isPPK
    ) {
      label = `Cost: ${toMoney(selectedAmount.premium)}`;
    }
  }
  return <div className="text-xl font-normal">{label}</div>;
}

function BondAmount({ selectedAmountId, setSelectedAmountId, product }) {
  if (product.amounts.length === 1) return null;
  return (
    <div className="my-7">
      <label className="block font-semibold text-gray-600 mb-1">
        Bond Amount
      </label>

      <select
        value={selectedAmountId}
        className="focus:ring-blue-surety focus:border-blue-surety block w-full shadow-sm border-gray-300 rounded-md"
        onChange={(e) => setSelectedAmountId(e.target.value)}
      >
        <option value="" disabled>
          Select a Bond Amount
        </option>
        {product.amounts.map((amount, i) => (
          <option key={amount._id} value={amount._id}>
            {formatAmount(amount)}
          </option>
        ))}
      </select>
    </div>
  );
}

// Get StaticPaths from /:stateName/:bondName
export async function getStaticPaths(props) {
  const siteData = await getSiteData();
  const paths = [];
  Object.keys(siteData.stateTable).forEach((stateName) => {
    const stateProducts = siteData.stateTable[stateName].products.map(
      (p) => siteData.productTable[p]
    );
    stateProducts.forEach((product) => {
      const path = `/${stateName}/${product.slug}`;
      paths.push(path);
    });
  });
  return {
    paths,
    fallback: false,
  };
}

function AdditionalInfoItem({ title, children, defaultOpen, locked }) {
  return (
    <Disclosure defaultOpen={defaultOpen}>
      {({ open }) => (
        <>
          <Disclosure.Button
            disabled={locked}
            className="flex justify-between w-full items-center py-3 font-medium text-left"
          >
            <span className="text-2xl font-semibold ">{title}</span>
            {!locked && !defaultOpen && (
              <ChevronUpIcon
                className={`${
                  open ? 'transform rotate-180' : ''
                } w-5 h-5 text-gray-800`}
              />
            )}
          </Disclosure.Button>

          <Disclosure.Panel className="px-2 py-3">{children}</Disclosure.Panel>
        </>
      )}
    </Disclosure>
  );
}

function formatAmount(amount) {
  let display = toMoney(amount.low);
  if (amount.type === 'range') {
    display += ` - ${toMoney(amount.high)}`;
  }
  return display;
}

function AdditionalInfo({ product, license }) {
  return (
    <div className="sm:w-7/12 w-full px-6 text-lg divide-y divide-gray-800">
      <AdditionalInfoItem defaultOpen={true} title={'Bond Information'}>
        <dl>
          <dt className="float-left mr-2 font-semibold">State:</dt>
          <dd>{product.state}</dd>
          <dt className="float-left mr-2 font-semibold">
            Amount{product.amounts.length > 1 ? 's' : ''}:
          </dt>
          <dd>{product.amounts.map((a) => formatAmount(a)).join(',')}</dd>
          <dt className="float-left mr-2 font-semibold">Term:</dt>
          <dd>{product.term}</dd>
          <dt className="float-left mr-2 font-semibold">Licensing Agent:</dt>
          <dd>{license.agent}</dd>
          <dt className="float-left mr-2 font-semibold">License Number:</dt>
          <dd>{license.licenseNumber}</dd>
        </dl>
      </AdditionalInfoItem>
      {product.bondDetails && (
        <AdditionalInfoItem title={'Bond Details'}>
          <div className="prose prose-blue">
            <MarkdownContent markdown={product.bondDetails}></MarkdownContent>
          </div>
        </AdditionalInfoItem>
      )}
      {product.licensingInformation && (
        <AdditionalInfoItem title={'Licensing Information'}>
          <div className="prose prose-blue">
            <MarkdownContent
              markdown={product.licensingInformation}
            ></MarkdownContent>
          </div>
        </AdditionalInfoItem>
      )}
    </div>
  );
}

function PerkItem({ icon, title, text }) {
  return (
    <div className="flex w-full flex-col items-center justify-between space-y-12">
      <div className="flex-col max-w-md items-center text-center sm:items-start sm:text-left flex w-full py-6">
        <div className="flex flex-row items-center space-x-3">
          <div className="h-6 w-6 flex items-center justify-center">{icon}</div>
          <div className="font-semibold font-brand">{title}</div>
        </div>
        <p className="font-medium mt-2">{text}</p>
      </div>
    </div>
  );
}
function ProductPerks({ product }) {
  const hasUnderwrittenAmounts =
    product.amounts.filter((a) => !a.instantPurchase).length > 0;
  const emailedBond = product.originalRequired;

  let dynamicText = '';
  if (hasUnderwrittenAmounts) {
    if (emailedBond)
      dynamicText =
        "We've streamlined the bondig process to make your experience as fast and easy as possible.";
    else {
      dynamicText = (
        <>
          Once you purchase your bond, we'll email your bond documents directly
          to you. <em>No shipping required.</em>{' '}
        </>
      );
    }
  } else {
    dynamicText = (
      <>
        Purchase your bond <em>today</em> and we'll email your bond directly to
        you. <strong>No shipping required.</strong>
      </>
    );
  }
  return (
    <div className="flex-1 sm:pt-0 pt-6">
      <PerkItem
        icon={<ShieldCheckIcon />}
        title="Secure Checkout"
        text=" Backed by SSL, our online bonding process is secure. We are
            committed to your privacy."
      ></PerkItem>
      <hr className="max-w-md " />
      <PerkItem
        icon={
          hasUnderwrittenAmounts && !emailedBond ? <ClockIcon /> : <MailIcon />
        }
        title={hasUnderwrittenAmounts && !emailedBond ? 'Fast' : 'Email'}
        text={dynamicText}
      ></PerkItem>
      <hr className="max-w-md" />
      <PerkItem
        icon={<TagIcon />}
        title="Best Prices"
        text="We shop the top surety markets to find you the best rate."
      ></PerkItem>
    </div>
  );
}

// Get Static Props for /:stateName/:bondNAme
export async function getStaticProps({
  params: { stateName, bondName },
  preview,
}) {
  const siteData = await getSiteData();
  const state = siteData.stateTable[stateName];
  const product = preview
    ? await getPreviewProductFromDato({ slug: bondName, state: stateName })
    : state.products
        .map((p) => siteData.productTable[p])
        .find((p) => p.slug === bondName);

  const license = licenses.find((l) => l.name === product.state);
  return {
    props: {
      product,
      license,
      stateName,
    },
  };
}
